// Vistas del organizador y planificación de rondas.

import { gql } from 'graphql-request';
import { getGraphQLQueryClient } from '../query-client';
import {
  Nullable,
  Tournament,
  UserWithToken,
  WithMeta,
} from '../api.types';

export type TournamentForImageGenerator = {
  id: number;
  title: string;
  dateFrom: string | null;
  organizerName: string | null;
  formatName: string | null;
  results: Array<{
    position: number;
    playerName: string;
  }>;
};

export const getTournamentsForImageGenerator = (
  user: Nullable<UserWithToken>
) =>
  getGraphQLQueryClient(user).request<{
    data: TournamentForImageGenerator[];
  }>(gql`
    query tournamentsForImageGenerator {
      data: tournamentsForImageGenerator {
        id
        title
        dateFrom
        organizerName
        formatName
        results {
          position
          playerName
        }
      }
    }
  `);

export const myOrganizerTournaments = async (
  user: Nullable<UserWithToken>,
  filters: Record<string, unknown> = {}
) =>
  getGraphQLQueryClient(user).request<WithMeta<Tournament>>(
    gql`
      query myOrganizerTournaments($filters: TournamentFilters) {
        data: myOrganizerTournaments(filters: $filters) {
          data {
            id
            title
            dateFrom
            dateTo
            totalPlayers
            rounds
            currentRound
            status
            tournamentType
            gameTypeId

            createdByUserId
            swissId
            withTop

            topTournaments {
              id
              status
            }

            results {
              id
              userId
              position
              points
              wins
              losses
              draws
            }

            location {
              name
              address
            }

            organizer {
              id
              name
              logoUrl
            }

            format {
              id
              name
            }

            players {
              id
              status
            }
          }
          meta {
            total
            perPage
            lastPage
          }
        }
      }
    `,
    { filters }
  );

// Round Planning types
export type TournamentRoundPlanning = {
  id: number;
  tournamentId: number;
  round: number;
  startDate: string;
  endDate: string;
  actualStartDate: string | null;
  duration: number | null;
  createdAt: string;
  updatedAt: string;
};

export type RoundPlanningInput = {
  round: number;
  startDate: string;
  endDate: string;
  duration?: number;
};

// Round Planning queries
export const getTournamentRoundPlannings = async (
  user: Nullable<UserWithToken>,
  tournamentId: number
) =>
  getGraphQLQueryClient(user).request<{
    data: TournamentRoundPlanning[];
  }>(
    gql`
      query tournamentRoundPlannings($tournamentId: Int!) {
        data: tournamentRoundPlannings(tournamentId: $tournamentId) {
          id
          tournamentId
          round
          startDate
          endDate
          actualStartDate
          duration
          createdAt
          updatedAt
        }
      }
    `,
    { tournamentId }
  );

export const getTournamentRoundPlanning = async (
  user: Nullable<UserWithToken>,
  tournamentId: number,
  round: number
) =>
  getGraphQLQueryClient(user).request<{
    data: TournamentRoundPlanning | null;
  }>(
    gql`
      query tournamentRoundPlanning($tournamentId: Int!, $round: Int!) {
        data: tournamentRoundPlanning(
          tournamentId: $tournamentId
          round: $round
        ) {
          id
          tournamentId
          round
          startDate
          endDate
          actualStartDate
          duration
          createdAt
          updatedAt
        }
      }
    `,
    { tournamentId, round }
  );

/**
 * Las rondas del top de un suizo (`round` = la del top: 1 = primera
 * instancia). Viven en el top si ya existe; si no, colgadas del suizo.
 */
export const getTournamentTopRoundPlannings = async (
  user: Nullable<UserWithToken>,
  tournamentId: number
) =>
  getGraphQLQueryClient(user).request<{
    data: TournamentRoundPlanning[];
  }>(
    gql`
      query tournamentTopRoundPlannings($tournamentId: Int!) {
        data: tournamentTopRoundPlannings(tournamentId: $tournamentId) {
          id
          tournamentId
          round
          startDate
          endDate
          actualStartDate
          duration
          createdAt
          updatedAt
        }
      }
    `,
    { tournamentId }
  );

// Round Planning mutations
export const setRoundPlannings = async (
  user: Nullable<UserWithToken>,
  tournamentId: number,
  plannings: RoundPlanningInput[],
  /** Las rondas del top; sin pasarlo no se tocan. */
  topPlannings?: RoundPlanningInput[]
) =>
  getGraphQLQueryClient(user).request<{
    data: TournamentRoundPlanning[];
  }>(
    gql`
      mutation setRoundPlannings(
        $tournamentId: Int!
        $plannings: [RoundPlanningInput!]!
        $topPlannings: [RoundPlanningInput!]
      ) {
        data: setRoundPlannings(
          tournamentId: $tournamentId
          plannings: $plannings
          topPlannings: $topPlannings
        ) {
          id
          tournamentId
          round
          startDate
          endDate
          actualStartDate
          duration
          createdAt
          updatedAt
        }
      }
    `,
    { tournamentId, plannings, topPlannings }
  );

export const updateRoundPlanning = async (
  user: Nullable<UserWithToken>,
  tournamentId: number,
  round: number,
  input: { startDate?: string; endDate?: string; duration?: number }
) =>
  getGraphQLQueryClient(user).request<{
    data: TournamentRoundPlanning;
  }>(
    gql`
      mutation updateRoundPlanning(
        $tournamentId: Int!
        $round: Int!
        $input: UpdateRoundPlanningInput!
      ) {
        data: updateRoundPlanning(
          tournamentId: $tournamentId
          round: $round
          input: $input
        ) {
          id
          tournamentId
          round
          startDate
          endDate
          actualStartDate
          duration
          createdAt
          updatedAt
        }
      }
    `,
    { tournamentId, round, input }
  );

export const startRound = async (
  user: Nullable<UserWithToken>,
  tournamentId: number,
  round: number
) =>
  getGraphQLQueryClient(user).request<{
    data: TournamentRoundPlanning;
  }>(
    gql`
      mutation startRound($tournamentId: Int!, $round: Int!) {
        data: startRound(tournamentId: $tournamentId, round: $round) {
          id
          tournamentId
          round
          startDate
          endDate
          actualStartDate
          duration
          createdAt
          updatedAt
        }
      }
    `,
    { tournamentId, round }
  );

export const setStreamMatch = async (
  user: Nullable<UserWithToken>,
  tournamentId: number,
  matchId: number | null
) =>
  getGraphQLQueryClient(user).request<{ data: Tournament }>(
    gql`
      mutation setStreamMatch($tournamentId: Int!, $matchId: Int) {
        data: setStreamMatch(tournamentId: $tournamentId, matchId: $matchId) {
          id
          streamMatchId
        }
      }
    `,
    { tournamentId, matchId }
  );

/**
 * Si la tienda transmite (sesiones, overrides del panel o configuración de
 * streaming del organizador). Las pantallas del organizador lo usan para
 * mostrar o no los controles de transmisión en cada mesa. Va aparte de
 * `getTournament` porque cuesta unas cuentas en la base y sólo lo necesita el
 * organizador.
 */
export const getTournamentUsesStreaming = async (
  user: Nullable<UserWithToken>,
  tournamentId: number
) =>
  getGraphQLQueryClient(user).request<{
    data: { id: number; usesStreaming: boolean } | null;
  }>(
    gql`
      query tournamentUsesStreaming($id: Int!) {
        data: tournament(id: $id) {
          id
          usesStreaming
        }
      }
    `,
    { id: tournamentId }
  );

export const deleteRoundPlannings = async (
  user: Nullable<UserWithToken>,
  tournamentId: number
) =>
  getGraphQLQueryClient(user).request<{
    data: boolean;
  }>(
    gql`
      mutation deleteRoundPlannings($tournamentId: Int!) {
        data: deleteRoundPlannings(tournamentId: $tournamentId)
      }
    `,
    { tournamentId }
  );
